import PropTypes from "prop-types"
import React, { useState } from "react"
import { useQueryClient } from "react-query"
import MergeInvoiceModal from "src/main/Billing/Payments/MergeInvoiceModal"
import ReversePaymentConfirmationModal from "src/main/Billing/Payments/ReversePaymentConfirmationModal"
import SettlePaymentModal from "src/main/Billing/Payments/SettlePaymentModal"
import ReversePaymentButton from "src/main/Billing/ReversePaymentButton"
import { SharedBillingContext } from "src/main/Billing/SharedBillingContext"
import { useBillingSheets } from "src/main/Billing/Sheets/context"
import { invalidateBillingSurfaces } from "src/main/Billing/invalidateBillingSurfaces"

import ActionMenu from "src/components/ActionMenu"

import EditInvoiceDetailsModal from "./EditInvoiceDetailsModal"
import WithInvoiceActionContext from "./WithInvoiceActionContext"
import { useInvoiceTracker } from "./useInvoiceTracker"

const InvoiceActionsMenu = ({ invoice }) => {
  const queryClient = useQueryClient()
  const sheets = useBillingSheets()
  const track = useInvoiceTracker()
  const [openAction, setOpenAction] = useState(null)

  const open = (action, event) => {
    track(event)
    setOpenAction(action)
  }
  const close = () => setOpenAction(null)
  const closeAndReload = () => {
    close()
    invalidateBillingSurfaces(queryClient)
  }
  const showMergedInto = (destination) => {
    closeAndReload()
    const destinationPath = destination?.detail_path
    if (sheets?.isInvoiceOpen(invoice.id)) {
      sheets.replaceInvoice(invoice.id, destinationPath ? destination.id : null)
    } else {
      window.location.assign(destinationPath ?? invoice.sourcePath)
    }
  }

  return (
    <div className="flex items-center gap-2">
      {invoice.canReverse && (
        <ReversePaymentButton
          disabledMessage={invoice.reverseDisabledMessage}
          onClick={() => open("reverse", "reverse_payment_pressed")}
        />
      )}
      <ActionMenu
        triggerVariant="standalone"
        items={[
          invoice.canSettle && {
            label: "Settle",
            icon: "money-bills",
            iconStyle: "solid",
            onClick: () => open("settle", "settle_pressed"),
          },
          invoice.canEditDetails && {
            label: "Edit details",
            icon: "pen",
            iconStyle: "solid",
            onClick: () => open("details", "edit_details_pressed"),
          },
          invoice.canMerge && {
            label: "Merge invoices",
            icon: "merge",
            iconStyle: "solid",
            disabled: Boolean(invoice.mergeDisabledReason),
            disabledReason: invoice.mergeDisabledMessage,
            onClick: () => open("merge", "merge_pressed"),
          },
          {
            label: `View customer ${invoice.printableDocument}`,
            icon: "receipt",
            iconStyle: "solid",
            href: invoice.receiptPath,
            onClick: () =>
              track("printable_pressed", {
                document: invoice.printableDocument,
              }),
          },
          invoice.sourcePath !== window.location.pathname && {
            label: `View ${invoice.kindLabel.toLowerCase()}`,
            icon: "arrow-up-right-from-square",
            iconStyle: "solid",
            href: invoice.sourcePath,
            onClick: () => track("view_source_pressed"),
          },
        ].filter(Boolean)}
      />
      {openAction && (
        <WithInvoiceActionContext invoiceId={invoice.id} onError={close}>
          {(context) => (
            <>
              {openAction === "settle" && (
                <SharedBillingContext.Provider
                  value={{
                    cardReaders: context.card_readers,
                    enableBluetoothCardReader:
                      context.enable_bluetooth_card_reader,
                    contractSurchargeEligible: context.surcharge_eligible,
                  }}
                >
                  <SettlePaymentModal
                    payment={context.invoice}
                    paymentMethods={context.payment_methods}
                    onPaymentSettle={closeAndReload}
                    isOpen
                    onClose={close}
                  />
                </SharedBillingContext.Provider>
              )}
              {openAction === "details" && (
                <EditInvoiceDetailsModal
                  context={context}
                  onClose={close}
                  onSaved={closeAndReload}
                />
              )}
              {openAction === "merge" && (
                <MergeInvoiceModal
                  invoice={context.invoice}
                  availableInvoices={[
                    context.invoice,
                    ...context.merge_candidates,
                  ]}
                  isOpen
                  onClose={close}
                  onMergeSubmitSuccess={({ destinationInvoiceId }) =>
                    showMergedInto(
                      context.merge_candidates.find(
                        (candidate) => candidate.id === destinationInvoiceId
                      )
                    )
                  }
                />
              )}
              {openAction === "reverse" && (
                <ReversePaymentConfirmationModal
                  isOpen
                  onClose={close}
                  onReverseSuccess={closeAndReload}
                  reservationId={context.container.reservation_id ?? undefined}
                  saleId={context.container.sale_id ?? undefined}
                  paymentId={invoice.id}
                  paymentTxnId={invoice.reversePaymentTxnId ?? undefined}
                  examples={invoice.reversalExamples}
                  invoices={
                    invoice.reversePaymentTxnId
                      ? context.reverse_invoices.map((row) => ({
                          invoiceId: row.invoice_id,
                          displayId: row.display_id,
                          kindLabel: row.kind_label,
                          coveredCents: row.covered_cents,
                        }))
                      : undefined
                  }
                />
              )}
            </>
          )}
        </WithInvoiceActionContext>
      )}
    </div>
  )
}

InvoiceActionsMenu.propTypes = {
  invoice: PropTypes.shape({
    id: PropTypes.string.isRequired,
    kindLabel: PropTypes.string.isRequired,
    sourcePath: PropTypes.string.isRequired,
    receiptPath: PropTypes.string.isRequired,
    printableDocument: PropTypes.oneOf(["receipt", "invoice"]).isRequired,
    canSettle: PropTypes.bool.isRequired,
    canMerge: PropTypes.bool.isRequired,
    canEditDetails: PropTypes.bool.isRequired,
    mergeDisabledReason: PropTypes.string,
    mergeDisabledMessage: PropTypes.string,
    canReverse: PropTypes.bool.isRequired,
    reverseDisabledMessage: PropTypes.string,
    reversePaymentTxnId: PropTypes.string,
    reversalExamples: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
}

export default InvoiceActionsMenu
